<script context="module" lang="ts">
	import { z } from "zod";
	export const name = "default view";

	export const CustomFields = z.object({
		show_summary: z.boolean().default(false),
	});
</script>

<script lang="ts">
	import Data from "@reperio-news/core.components/blocks/ContentItem/Data";
	import { stripTags } from "@reperio-news/core.components/includes/Helpers";
	type Data = z.infer<typeof Data>;
	import { getContext } from "svelte";
	import ImageView from "@reperio-news/core.components/ImageView.svelte";
	import { slugToColor } from "../TopMenu/View.svelte";
	import { getURLRel } from "../../includes/helpers.svelte";
	import TitleTag from "./TitleTag.svelte";

	export let data: Data = {};
	const c: SvelteStore<number | null> = getContext("parentBlockColumnCount");
	const context: SvelteStore<any> = getContext("context");

	let categoryColor: string = undefined;
	let externalDomain: string = undefined;

	$: if (
		data &&
		data.content &&
		data.content.url &&
		$context &&
		$context.global_blocks &&
		$context.global_blocks.TopMenu_main_menu
	) {
		categoryColor = slugToColor(
			data.content.url,
			$context.global_blocks?.TopMenu_main_menu?.data?.items,
		);

		try {
			const x = new URL(data.content.url);
			externalDomain = x.hostname;
		} catch (_e) {
			if (data.content.url.startsWith("/")) {
				externalDomain = $context.domain;
			}
		}
	}
</script>

{#if data && data.content}
	<div class="entry co-{$c}">
		{#if data.content.poster}
			<a
				class="image"
				href={data.content.url}
				title={data.content.summary}
				rel={getURLRel(data.content.url)}
			>
				<ImageView
					src={data.content.poster}
					crop={data.content.poster.crop}
					width={$c === 1 ? 900 : 500}
					height={$c === 1 ? 400 : 300}
					widths={$c === 1 ? [900, 720, 600, 450] : [500, 400, 300]}
					heights={$c === 1 ? [400, 320, 267, 200] : [300, 240, 180]}
					animatedQuality={45}
					fit="cover"
				/>
			</a>
		{/if}

		<div class="mainInfo">
			<div class="of">
				{#if data.content.topic}
					<a
						class="topic"
						href="//{data.content.domain}/{data.content.topic.slug}"
						title="Ultimele știri - {data.content.topic.name}"
					>
						#{data.content.topic.name}
					</a>
				{/if}
			</div>

			<a
				class="title"
				href={data.content.url}
				title={stripTags(data.content.summary)}
				rel={getURLRel(data.content.url)}
				><TitleTag content={data.content} />{@html stripTags(
					data.content.title,
					["i", "b", "strong", "font"],
				)}</a
			>

			{#if data.custom_fields && data.custom_fields.show_summary && data.content.summary}
				<div class="summary">
					{@html stripTags(data.content.summary, ["i", "b", "strong", "font"])}
				</div>
			{/if}

			{#if data.content.category && externalDomain === "click.ro"}
				<a
					class="category"
					style={categoryColor
						? `color: ${categoryColor}`
						: ""}
					href="//{data.content.domain}/{data.content.category.slug}"
					title="Stiri click.ro - {data.content.category.name.toLowerCase()}"
				>
					{data.content.category.name}
				</a>
			{:else if externalDomain && externalDomain !== "click.ro"}
				<span class="category external">
					{externalDomain}
				</span>
			{/if}
		</div>
	</div>
{/if}

<style lang="scss">
	.co-1 {
		flex-grow: 0 !important;
		width: calc(100% - 20px);
		.mainInfo a.title {
			padding: 5px;
			font-size: 1.3rem;
			text-align: left;
			-webkit-line-clamp: 10;
		}
	}

	.co-2 {
		width: calc(50% - 30px);
		.mainInfo .title {
			font-size: 1.1rem !important;
			-webkit-line-clamp: 3;
		}
	}

	.co-3 {
		width: calc(33% - 30px);
		.mainInfo .title {
			font-size: 1.05rem !important;
			-webkit-line-clamp: 2;
		}
	}

	.co-4 {
		width: calc(25% - 30px);
		.mainInfo .title {
			font-size: 1.2em !important;
		}
	}

	.entry:hover {
		box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
		transform: translateY(-2px);

		.image :global(img) {
			transform: scale(1.04);
		}

		.mainInfo .title {
			color: #111;
		}
	}

	.entry {
		display: flex;
		flex-direction: column;
		min-width: 300px;
		flex-grow: 1;
		color: inherit;
		margin: 10px;
		border: 1px solid #f0f0f0;
		border-radius: 8px;
		background-color: #fff;
		color: #111;
		max-width: 600px;
		position: relative;
		overflow: hidden;
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
		transition:
			box-shadow 0.25s ease,
			transform 0.25s ease;

		.image {
			min-width: 250px;
			/* box ratio matches the requested image ratio so object-fit
			   never crops a second time on top of the server-side crop */
			aspect-ratio: 5 / 3;
			overflow: hidden;
			display: flex;
			justify-content: center;
			align-items: center;

			:global(img) {
				transition: transform 0.35s ease;
			}
		}

		&.co-1 .image {
			aspect-ratio: 9 / 4;
		}

		.mainInfo {
			display: flex;
			flex-direction: column;
			align-items: center;
			flex: 1;
			padding: 16px 10px 0;
			text-align: center;
			position: relative;

			.of {
				position: absolute;
				top: -10px;
				left: 5px;
				width: 100%;
				display: flex;
				justify-content: center;
			}

			.category {
				font-weight: 700;
				font-family: "Archivo Narrow", sans-serif;
				font-size: 0.65rem;
				text-transform: uppercase;
				letter-spacing: 1px;
				color: #c00;
				border-top: 1px solid #f0f0f0;
				padding: 8px 0;
				margin-top: auto;
				text-align: center;
				display: block;
				width: 100%;
				box-sizing: border-box;

				&.external {
					color: #888;
				}
			}

			.title {
				font-family: "Oswald", sans-serif;
				font-style: normal;
				font-weight: 500;
				color: #1a1a1a;
				font-size: 1.35em;
				line-height: 1.25;
				transition: color 0.2s ease;

				overflow: hidden;
				display: -webkit-box;
				-webkit-box-orient: vertical;
				text-overflow: hidden;
				white-space: normal;
				text-overflow: ellipsis;
			}

			.summary {
				overflow: hidden;
				display: -webkit-box;
				-webkit-box-orient: vertical;
				text-overflow: hidden;
				white-space: normal;
				text-overflow: ellipsis;
				width: calc(100% - 10px);
				margin: auto;
				padding: 5px;
				text-align: left;
				font-size: 1.07rem;
				-webkit-line-clamp: 2;
				font-weight: 300;
				height: 48px;
				font-family: "Source Sans Pro", sans-serif;
			}
		}

		.topic {
			background-color: rgb(187, 0, 0);
			color: #fff;
			text-align: center;
			padding: 2px 6px;
			display: inline-block;
			text-transform: uppercase;
			border-radius: 2px;
			font-size: 0.8em;
			letter-spacing: 0.3px;
		}

	}

	:global {
		.carousel-news {
			div.entry {
				overflow: hidden;

				min-width: calc(var(--parent-width) / 3 - 20px) !important;

				a {
					-webkit-line-clamp: 2;
				}

				.summary {
					-webkit-line-clamp: 2;
				}
			}
		}
	}

	@media only screen and (max-width: 1000px) {
		.co-1 {
			width: calc(100% - 30px);
			max-width: 100%;
		}
	}

	@media only screen and (max-width: 650px) {
		.entry {
			.image {
				height: auto;
			}
		}

		.co-1 {
			width: calc(100% - 30px);
		}

		:global {
			.carousel-news {
				div.entry {
					min-width: calc(var(--parent-width) - 21px) !important;
				}
			}
		}
	}

	@media only screen and (max-width: 500px) {
		.entry {
			border-radius: 6px;
			margin: 8px 6px;
		}
	}
</style>
